'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Event', href: '/event' },
  { label: 'Membership', href: '/membership' },
  { label: 'Sponsorship', href: '/sponsorship' },
  { label: 'About', href: '/about' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Playlist', href: '/playlist' },
];

export default function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  // These routes have no hero image under the header, so a transparent-
  // until-scrolled header is unreadable there — force it solid regardless
  // of scroll position.
  const noHeroRoutes = [
    '/join',
    '/terms',
    '/privacy-policy',
    '/cookie-policy',
    '/sponsorship/thank-you',
  ];
  const noHero = noHeroRoutes.some((route) => pathname.startsWith(route));
  const isSolid = scrolled || noHero;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isSolid
            ? 'bg-dark-bg/95 backdrop-blur-xl shadow-dark border-b border-accent/10'
            : 'bg-transparent'
        }`}
      >
        <div
          className={`w-full px-[8%] md:px-[15.6%] flex items-center justify-between gap-4 transition-[padding] duration-300 ${
            isSolid ? 'py-2 md:py-2.5' : 'py-3 md:py-4'
          }`}
        >
          {/* Logo */}
          <Link href="/" className="flex items-center group flex-shrink-0">
            <AppLogo variant="light" size={isSolid ? 140 : 170} />
          </Link>

          {/* Desktop Nav — centered via flex, no overlap with logo/CTA */}
          <nav className="hidden lg:flex items-center justify-center gap-8 flex-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm font-semibold tracking-wide transition-colors duration-200 relative group whitespace-nowrap ${
                  isActive(link.href) ? 'text-accent' : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-accent transition-all duration-300 ${
                    isActive(link.href) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <Link
              href="/join"
              className="hidden lg:inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm font-bold tracking-wide hover:bg-secondary transition-colors duration-200 shadow-crimson"
            >
              Join Now
            </Link>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="lg:hidden flex flex-col gap-1.5 p-2 group"
            >
              <span
                className={`block h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'w-6 rotate-45 translate-y-2' : 'w-6'
                }`}
              />
              <span
                className={`block h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'opacity-0 w-0' : 'w-4'
                }`}
              />
              <span
                className={`block h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'w-6 -rotate-45 -translate-y-2' : 'w-6'
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-dark-bg backdrop-blur-xl flex flex-col transition-all duration-500 lg:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col items-center justify-center flex-1 gap-8 pt-20">
          {navLinks.map((link, i) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className={`text-2xl font-bold tracking-tight transition-colors duration-200 ${
                isActive(link.href) ? 'text-accent' : 'text-white/90 hover:text-accent'
              }`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/join"
            onClick={() => setMenuOpen(false)}
            className="mt-4 bg-primary text-primary-foreground px-10 py-4 rounded-full text-base font-bold tracking-wide hover:bg-secondary transition-colors shadow-crimson"
          >
            Become a Member
          </Link>
        </div>
        <div className="pb-10 text-center">
          <p className="text-white/30 text-xs tracking-widest">PRATHAA 2026 · OCT 16-21 · MUMBAI</p>
        </div>
      </div>
    </>
  );
}
